import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Lock, AlertTriangle } from 'lucide-react';
import {
  asPercent,
  asPValue,
  asSigned,
  VERDICT_LABELS,
  VERDICT_STYLES,
} from '../../hooks/useMetricsApi';

const VerdictBadge = ({ verdict }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
      VERDICT_STYLES[verdict] || 'bg-slate-100 text-slate-600 border-slate-200'
    }`}
  >
    {VERDICT_LABELS[verdict] || verdict}
  </span>
);

// Improvement and regression are measured over DIFFERENT populations -- a
// student already at the best value cannot improve, one already at the worst
// cannot decline -- so they get separate bars with their own denominators
// printed. A single stacked bar would imply they share one, which is the
// mistake that hid every regression in the first place.
const RateBar = ({ label, n, eligible, rate, tone }) => {
  if (rate === null || rate === undefined) return null;
  const tones = {
    good: 'bg-emerald-500',
    bad: 'bg-red-500',
  };
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-slate-900">
          {asPercent(rate)}
          <span className="ml-1 font-normal text-slate-400">
            ({n} of {eligible})
          </span>
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
        <div
          className={`h-1.5 rounded-full ${tones[tone]}`}
          style={{ width: `${Math.min(100, Math.round(rate * 100))}%` }}
        />
      </div>
    </div>
  );
};

const Figure = ({ label, value, hint }) => {
  if (value === null || value === undefined) return null;
  return (
    <div>
      <dt className="text-xs text-slate-500" title={hint}>
        {label}
      </dt>
      <dd className="text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
};

const Distribution = ({ title, counts, valueLabels }) => {
  const entries = Object.entries(counts || {});
  if (!entries.length) return null;
  const highest = Math.max(...entries.map(([, count]) => count));
  return (
    <div>
      <p className="text-xs font-medium text-slate-600">{title}</p>
      <div className="mt-1 space-y-1">
        {entries.map(([value, count]) => (
          <div key={value} className="flex items-center gap-2">
            <span className="w-28 shrink-0 truncate text-xs text-slate-500">
              {valueLabels?.[value] ? `${value} ${valueLabels[value]}` : value}
            </span>
            <div className="h-2 flex-1 rounded-sm bg-slate-100">
              <div
                className="h-2 rounded-sm bg-slate-400"
                style={{ width: `${(count / highest) * 100}%` }}
              />
            </div>
            <span className="w-8 shrink-0 text-right text-xs text-slate-500">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MetricCard = ({ metric, definition }) => {
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const diagnostics = metric.diagnostics;

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{metric.label}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {metric.source === 'self_report' ? 'Student reported' : 'Clinician observed'}
            {definition?.scaleMax > 1 &&
              ` · scale ${definition.scaleMin}–${definition.scaleMax}`}
          </p>
        </div>
        <VerdictBadge verdict={metric.verdict} />
      </div>

      {metric.reportable ? (
        <div className="space-y-4 p-4">
          {/* The registry's own wording, filled in by the engine. Never
              paraphrased here: the sentence and the arithmetic are declared
              together so they cannot drift apart. */}
          <div className="space-y-1">
            {metric.sentences.map((sentence) => (
              <p key={sentence} className="text-sm text-slate-700">
                {sentence}
              </p>
            ))}
          </div>

          <div className="space-y-3">
            <RateBar
              label="Improved, of those with room to improve"
              n={metric.improved}
              eligible={metric.improvable}
              rate={metric.improvementRate}
              tone="good"
            />
            <RateBar
              label="Declined, of those who could decline"
              n={metric.declined}
              eligible={metric.declinable}
              rate={metric.declineRate}
              tone="bad"
            />
            {metric.declineRate === null && metric.declinable > 0 && (
              <p className="text-xs text-slate-400">
                Regression rate withheld: only {metric.declinable} student
                {metric.declinable === 1 ? '' : 's'} could have declined on this
                item.
              </p>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 sm:grid-cols-3">
            <Figure
              label="Held steady"
              value={metric.holding}
              hint="Already at the best value at intake, and still there"
            />
            <Figure
              label="Median change"
              value={asSigned(metric.medianDelta)}
              hint="Signed so that positive is always an improvement, whichever way the scale runs"
            />
            <Figure
              label="Middle half"
              value={
                metric.deltaIqr
                  ? `${asSigned(metric.deltaIqr[0])} to ${asSigned(metric.deltaIqr[1])}`
                  : null
              }
              hint="Interquartile range of the change: where the middle 50% of students landed"
            />
            {/* Only where there was room for degrees. With a single point of
                headroom this share can only be 0 or 1 per student, so its
                average is the improvement rate restated -- shown next to that
                rate it reads as a second, stronger result. */}
            <Figure
              label="Gap closed"
              value={metric.magnitudeMeaningful ? asPercent(metric.meanHeadroom) : null}
              hint="Of the improvement available at intake, the average share actually realised, over students who had room to improve. Negative if the group moved backwards."
            />
            <Figure
              label="Effect size"
              value={metric.effectSize === null ? null : metric.effectSize.toFixed(2)}
              hint="Matched-pairs rank-biserial correlation, from -1 to +1"
            />
            <Figure
              label="Significance"
              value={asPValue(metric.pValue)}
              hint="Wilcoxon signed-rank test over the students whose value changed"
            />
          </dl>

          {metric.headroomPoints !== null && metric.headroomPoints < 2 && (
            <p className="text-xs text-slate-400">
              Students who could improve had {metric.headroomPoints} scale point
              {metric.headroomPoints === 1 ? '' : 's'} of room on average, so this
              item records whether a student improved, not by how much.
            </p>
          )}

          <p className="text-xs text-slate-400">
            {metric.changed} students changed on this item{' · '}
            {asPercent(metric.coverage)} of students had a usable answer at both ends
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-2 p-4">
          {metric.verdict === 'quarantined' ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          ) : (
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          )}
          <div>
            <p className="text-sm text-slate-700">{metric.withheldBecause}</p>
            <p className="mt-1 text-xs text-slate-400">
              No figures are reported for this item.
            </p>
          </div>
        </div>
      )}

      {diagnostics && (
        <div className="border-t border-slate-100">
          <button
            type="button"
            onClick={() => setShowDiagnostics((open) => !open)}
            className="flex w-full items-center gap-1 px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            {showDiagnostics ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Data quality
          </button>
          {showDiagnostics && (
            <div className="space-y-3 bg-slate-50 px-4 pb-4 pt-1">
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <Figure label="Scored" value={diagnostics.scored} />
                <Figure label="Unscorable" value={diagnostics.unscorable} />
                <Figure
                  label="Not asked"
                  value={diagnostics.notAsked}
                  hint="The form did not carry this question on that date -- not missing data"
                />
                <Figure label="Unchanged" value={diagnostics.unchanged} />
                <Figure label="Could improve" value={diagnostics.improvable} />
                <Figure label="Could decline" value={diagnostics.declinable} />
              </dl>
              <Distribution
                title="Values at intake"
                counts={diagnostics.before}
                valueLabels={definition?.valueLabels}
              />
              <Distribution
                title="Values at outcome"
                counts={diagnostics.after}
                valueLabels={definition?.valueLabels}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { VerdictBadge };
export default MetricCard;
